import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, Heart, MessageCircle } from 'lucide-react';

const IG_HANDLE = "@weavesoflucknow";
const IG_URL = "https://www.instagram.com/weavesoflucknow";

const igPosts = [
  {
    img: "https://images.pexels.com/photos/28512776/pexels-photo-28512776.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
    likes: "1.2k", comments: "48",
    caption: "Authenticity in every stitch #Chikankari",
  },
  {
    img: "https://images.pexels.com/photos/28512773/pexels-photo-28512773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
    likes: "985", comments: "32",
    caption: "Lucknow ki riwayat, aaj ki nari",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/0aefe824-dd3a-42e7-88bf-f336a3227281/images/71d5c259f6c851459268b8cbc0e88d04f9129edb77e9d70905765b60fce0b655.png",
    likes: "2.1k", comments: "71",
    caption: "Details that tell centuries of stories",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/0aefe824-dd3a-42e7-88bf-f336a3227281/images/21f16657c96e3fc17e1622cdb3300552ed4e26ce78c8a54e84ccda2934e85891.png",
    likes: "3.4k", comments: "94",
    caption: "Meet the hands behind the magic",
  },
  {
    img: "https://images.unsplash.com/photo-1590242157838-a7dd8990b1c1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400&h=400&fit=crop",
    likes: "1.7k", comments: "55",
    caption: "Pure cotton, pure craft, pure love",
  },
  {
    img: "https://images.pexels.com/photos/6039902/pexels-photo-6039902.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
    likes: "2.8k", comments: "83",
    caption: "Confidence looks good on you",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

export default function InstagramFeedSection() {
  return (
    <section data-testid="ig-feed" className="py-24 md:py-32 bg-[#E8DED1]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12 } } }}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.span variants={fadeUp} className="inline-block text-xs md:text-sm font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium mb-4">
            Hamare Saath Judiye
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-serif font-light text-[#2D4A2D] text-4xl md:text-5xl leading-tight tracking-tight mb-4">
            Follow us on Instagram
          </motion.h2>
          <motion.a
            variants={fadeUp}
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ig-follow-btn"
            className="inline-flex items-center gap-2 font-sans font-medium text-[#2D4A2D] text-sm hover:text-[#C9A84C] transition-colors duration-200 group"
          >
            <Instagram size={18} strokeWidth={1.5} className="group-hover:scale-110 transition-transform duration-200" />
            {IG_HANDLE}
          </motion.a>
        </motion.div>

        {/* Instagram Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
          {igPosts.map((post, i) => (
            <motion.a
              key={post.caption}
              href={IG_URL}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-30px" }}
              transition={{ delay: i * 0.08 }}
              className="relative overflow-hidden group aspect-square block"
              data-testid={`ig-post-${i}`}
            >
              <img
                src={post.img}
                alt={post.caption}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-[#1A2A1A]/0 group-hover:bg-[#1A2A1A]/65 transition-all duration-400 flex flex-col items-center justify-center gap-3">
                <div className="flex items-center gap-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                  <span className="flex items-center gap-1.5 text-[#F5F0E8] font-sans text-sm font-medium">
                    <Heart size={16} fill="#F5F0E8" stroke="none" />
                    {post.likes}
                  </span>
                  <span className="flex items-center gap-1.5 text-[#F5F0E8] font-sans text-sm font-medium">
                    <MessageCircle size={16} strokeWidth={1.5} className="text-[#F5F0E8]" />
                    {post.comments}
                  </span>
                </div>
                <p className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150 font-serif italic text-[#F5F0E8] text-sm text-center px-4 leading-snug">
                  {post.caption}
                </p>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Follow CTA button */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center mt-10"
        >
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ig-follow-cta"
            className="inline-flex items-center gap-2.5 border border-[#2D4A2D] text-[#2D4A2D] px-8 py-4 font-sans font-medium text-sm tracking-wide transition-all duration-300 hover:bg-[#2D4A2D] hover:text-[#F5F0E8]"
          >
            <Instagram size={18} strokeWidth={1.5} />
            Follow on Instagram
          </a>
        </motion.div>
      </div>
    </section>
  );
}
